"use client"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
    ExternalLink, Copy, Check, Globe, Shield, FileCode,
    Coins, Lock, Rocket, Layers, Wallet, Server
} from "lucide-react"
import { useState, useCallback } from "react"

/* ─── Explorer URLs ─── */
const explorers: Record<string, string> = {
    // Mainnet
    "Ethereum": "https://etherscan.io",
    "BSC": "https://bscscan.com",
    "Polygon": "https://polygonscan.com",
    "Base": "https://basescan.org",
    // Testnet
    "BSC Testnet": "https://testnet.bscscan.com",
    "Sepolia": "https://sepolia.etherscan.io",
    "Polygon Amoy": "https://amoy.polygonscan.com",
    "Base Sepolia": "https://sepolia.basescan.org",
}

/* ─── Contract Data (Mainnet) ─── */
const mainnetContracts = [
    {
        category: "Platform Core",
        icon: Server,
        contracts: [
            {
                name: "TokenDeployerProxy",
                description: "UUPS proxy for deploying custom ERC-20 tokens with configurable features.",
                addresses: [
                    { network: "Ethereum", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "BSC", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "Polygon", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "Base", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                ],
            },
            {
                name: "PresaleFactory",
                description: "Creates and manages presale contracts for token launches.",
                addresses: [
                    { network: "Ethereum", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "BSC", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "Polygon", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "Base", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                ],
            },
            {
                name: "FairLaunchFactory",
                description: "Deploys fair launch contracts with anti-snipe and equal opportunity mechanics.",
                addresses: [
                    { network: "Ethereum", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "BSC", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "Polygon", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "Base", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                ],
            },
            {
                name: "CollectorFactory",
                description: "Factory for creating contribution collector contracts.",
                addresses: [
                    { network: "Ethereum", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "BSC", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "Polygon", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "Base", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                ],
            },
            {
                name: "TokenLocker",
                description: "Securely locks LP tokens and ERC-20 tokens for specified durations.",
                addresses: [
                    { network: "Ethereum", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "BSC", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "Polygon", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "Base", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                ],
            },
        ],
    },
    {
        category: "TKS Token",
        icon: Coins,
        contracts: [
            {
                name: "TKS Token",
                description: "The native TokenKickstarter (TKS) ERC-20 token with reflection, staking, and governance features.",
                addresses: [
                    { network: "Ethereum", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4" },
                    { network: "BSC", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4" },
                ],
            },
            {
                name: "TKS Contribution Collector",
                description: "UUPS proxy managing TKS presale contributions across native tokens and stablecoins.",
                addresses: [
                    { network: "Ethereum", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee" },
                    { network: "BSC", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee" },
                    { network: "Polygon", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee" },
                    { network: "Base", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee" },
                ],
            },
            {
                name: "TKS Claim Vault",
                description: "UUPS proxy vault contract for distributing TKS tokens to presale participants.",
                addresses: [
                    { network: "Ethereum", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1" },
                    { network: "BSC", address: "0xa79289DB22eB16013d8044a64050691f4F3261C1" },
                ],
            },
            {
                name: "TKS Staking Master",
                description: "UUPS upgradeable staking contract with tiered rewards and allocation benefits.",
                addresses: [
                    { network: "Ethereum", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501" },
                    { network: "BSC", address: "0x6e3eb266d957a639982C4A47ec46586218cC1501" },
                ],
            },
        ],
    },
]

/* ─── Contract Data (Testnet) ─── */
const testnetContracts = [
    {
        category: "Platform Core",
        icon: Server,
        contracts: [
            {
                name: "TokenDeployerProxy",
                description: "UUPS proxy for deploying custom ERC-20 tokens with configurable features.",
                addresses: [
                    { network: "BSC Testnet", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "Polygon Amoy", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                    { network: "Base Sepolia", address: "0x0d3125192271Ec2bfB47cf6708b118D1858a8B12" },
                ],
            },
            {
                name: "PresaleFactory",
                description: "Creates and manages presale contracts for token launches.",
                addresses: [
                    { network: "BSC Testnet", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "Polygon Amoy", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                    { network: "Base Sepolia", address: "0x849bE86348d3E8A2Fb9273a911f5E2A970F3950D" },
                ],
            },
            {
                name: "FairLaunchFactory",
                description: "Deploys fair launch contracts with anti-snipe and equal opportunity mechanics.",
                addresses: [
                    { network: "BSC Testnet", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "Polygon Amoy", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                    { network: "Base Sepolia", address: "0x6D0A91511BAA649f5aeD8ab5cb65E1b3460d1BC5" },
                ],
            },
            {
                name: "CollectorFactory",
                description: "Factory for creating contribution collector contracts.",
                addresses: [
                    { network: "BSC Testnet", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "Polygon Amoy", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                    { network: "Base Sepolia", address: "0x86E6D585073F4C66fA3E739b830Ff2179207B657" },
                ],
            },
            {
                name: "TokenLocker",
                description: "Securely locks LP tokens and ERC-20 tokens for specified durations.",
                addresses: [
                    { network: "BSC Testnet", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "Polygon Amoy", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                    { network: "Base Sepolia", address: "0xa2e0848Fa0CCAb7488a6F70a93Deb01902401132" },
                ],
            },
        ],
    },
    {
        category: "TKS Token",
        icon: Coins,
        contracts: [
            {
                name: "TKS Token",
                description: "The native TokenKickstarter (TKS) ERC-20 token deployed for testnet.",
                addresses: [
                    { network: "BSC Testnet", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4" },
                    { network: "Sepolia", address: "0x420dbEf020711832d0a210be37270EFA8a1c00E4" },
                ],
            },
            {
                name: "TKS Contribution Collector",
                description: "UUPS proxy managing TKS presale contributions across native tokens and stablecoins.",
                addresses: [
                    { network: "BSC Testnet", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C" },
                    { network: "Sepolia", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C" },
                    { network: "Polygon Amoy", address: "0x59843f7e1B796a84c6CBC2f6529e8C59aFE92B3C" },
                    { network: "Base Sepolia", address: "0x8bC09932093Cff9019b7822B8835Be9Ad6ef91Ee" },
                ],
            },
            {
                name: "TKS Claim Vault",
                description: "UUPS proxy vault contract for distributing TKS tokens to presale participants.",
                addresses: [
                    { network: "BSC Testnet", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd" },
                    { network: "Sepolia", address: "0x38b95390f456db4E5308AfB455Ce6d643f7263bd" },
                ],
            },
            {
                name: "TKS Staking Master",
                description: "UUPS upgradeable staking contract with tiered rewards.",
                addresses: [
                    { network: "BSC Testnet", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366" },
                    { network: "Sepolia", address: "0xf035f4906fF6Eb79C2E183C307729F1f517EA366" },
                ],
            },
        ],
    },
]

/* ─── Copy Button ─── */
function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    const copy = useCallback(() => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
    }, [text])
    return (
        <button
            onClick={copy}
            className="p-1 rounded hover:bg-muted transition-colors shrink-0"
            title="Copy address"
        >
            {copied ? (
                <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            )}
        </button>
    )
}

/* ─── Address Row ─── */
function AddressRow({ network, address }: { network: string; address: string }) {
    const explorerUrl = explorers[network]
    return (
        <div className="flex items-center justify-between gap-2 py-2.5 border-b border-border/40 last:border-0">
            <div className="flex items-center gap-2 min-w-[120px] shrink-0">
                <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-medium">{network}</span>
            </div>
            <div className="flex items-center gap-1.5 min-w-0">
                <code className="text-xs text-primary font-mono truncate">{address}</code>
                <CopyButton text={address} />
                {explorerUrl && (
                    <a
                        href={`${explorerUrl}/address/${address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 rounded hover:bg-muted transition-colors shrink-0"
                        title="View on explorer"
                    >
                        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </a>
                )}
            </div>
        </div>
    )
}

/* ─── Contract Section ─── */
function ContractSection({ data }: {
    data: typeof mainnetContracts
}) {
    return (
        <div className="space-y-8">
            {data.map((group) => {
                const Icon = group.icon
                return (
                    <div key={group.category}>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                <Icon className="h-4 w-4 text-primary" />
                            </div>
                            <h3 className="text-lg font-semibold">{group.category}</h3>
                        </div>
                        <div className="space-y-4">
                            {group.contracts.map((contract) => (
                                <Card key={contract.name} className="hover:border-primary/30 transition-colors">
                                    <CardContent className="p-5">
                                        <div className="flex items-start justify-between mb-1">
                                            <h4 className="font-semibold">{contract.name}</h4>
                                            <Badge variant="secondary" className="text-[10px] shrink-0">Verified</Badge>
                                        </div>
                                        <p className="text-sm text-muted-foreground mb-3">{contract.description}</p>
                                        <div className="bg-muted/30 rounded-lg p-3">
                                            {contract.addresses.map((a) => (
                                                <AddressRow key={a.network} network={a.network} address={a.address} />
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

/* ─── Main Page ─── */
export function DeployedContractsContent() {
    const [tab, setTab] = useState<"mainnet" | "testnet">("mainnet")

    return (
        <div className="max-w-4xl">
            {/* Header */}
            <div className="mb-8">
                <Badge className="mb-4 gap-2">
                    <FileCode className="h-3 w-3" />
                    Smart Contracts
                </Badge>
                <h1 className="text-4xl font-bold tracking-tight mb-4">Deployed Contracts</h1>
                <p className="text-xl text-muted-foreground">
                    All TokenKickstarter smart contract addresses deployed across mainnet and testnet chains.
                    Every contract is verified and open-source.
                </p>
            </div>

            {/* Network Toggle */}
            <div className="flex gap-2 mb-8 p-1 bg-muted/50 rounded-lg w-fit">
                <button
                    onClick={() => setTab("mainnet")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === "mainnet"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                        }`}
                >
                    <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-green-500" />
                        Mainnet
                    </span>
                </button>
                <button
                    onClick={() => setTab("testnet")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === "testnet"
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                        }`}
                >
                    <span className="flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-yellow-500" />
                        Testnet
                    </span>
                </button>
            </div>

            {/* Info Banner */}
            <Card className="mb-8 border-primary/20 bg-primary/5">
                <CardContent className="p-4 flex items-start gap-3">
                    <Shield className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm text-muted-foreground">
                            {tab === "mainnet" ? (
                                <>All mainnet contracts are deployed across <strong>Ethereum, BSC, Polygon, and Base</strong>. Contracts use deterministic deployment for consistent addresses where possible.</>
                            ) : (
                                <>Testnet contracts are deployed on <strong>BSC Testnet, Sepolia, Polygon Amoy, and Base Sepolia</strong> for development and testing purposes.</>
                            )}
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Contract Sections */}
            {tab === "mainnet" ? (
                <ContractSection data={mainnetContracts} />
            ) : (
                <ContractSection data={testnetContracts} />
            )}

            {/* Footer Note */}
            <Card className="mt-10 bg-muted/30">
                <CardContent className="p-5">
                    <h4 className="font-semibold mb-2 text-sm">Important Notes</h4>
                    <ul className="text-sm text-muted-foreground space-y-1.5 list-disc list-inside">
                        <li>All contracts are verified on their respective block explorers.</li>
                        <li>UUPS proxy contracts can be upgraded by the platform owner for security patches.</li>
                        <li>Testnet contracts may be redeployed during development cycles.</li>
                        <li>Always verify addresses match the ones listed here before interacting.</li>
                    </ul>
                </CardContent>
            </Card>
        </div>
    )
}
